/**
 * Validierungstexte und Formular-Validatoren.
 *
 * Übernommen aus dem npm-Paket `anguland` (1.0.4), das beim Angular-22-Major
 * ausgebaut wurde: seine Peer-Range endete bei Angular 21 und liess sich per
 * npm-Override nicht aufweiten. Die Validatoren sind unverändert übernommen;
 * die Texte sind seit #1209 übersetzbar.
 */

import type { AbstractControl, ValidatorFn } from '@angular/forms';

export interface ValidationMessage {
  type: string;
  message: string;
}

export interface ValidationMessages {
  [key: string]: ValidationMessage[];
}

export const PASSWORD_MIN_LENGTH = 6;
export const USERNAME_MIN_LENGTH = 2;
export const USERNAME_MAX_LENGTH = 30;

// Texte stehen per $localize in den XLF-Dateien von customer-ui und website
// (#1209: vorher fest deutsch, auch in der EN-Oberfläche). Mehrfach genutzte
// Texte einmal als Konstante, damit jede ID genau einen Quelltext hat.
const UNKNOWN = $localize`:@@lib.validation.unknown:Unbekannter Fehler`;
const PASSWORD_REQUIRED = $localize`:@@lib.validation.password.required:Passwort erforderlich`;
const CREDENTIALS_WRONG = $localize`:@@lib.validation.credentialsWrong:Benutzername oder Passwort falsch`;

export const VALIDATION_MESSAGES: ValidationMessages = {
  username: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.username.required:Benutzername erforderlich`,
    },
    { type: 'unknown', message: UNKNOWN },
    {
      type: 'minlength',
      message: $localize`:@@lib.validation.username.minlength:Benutzername muss mindestens ${USERNAME_MIN_LENGTH}:min: Zeichen lang sein`,
    },
    {
      type: 'maxlength',
      message: $localize`:@@lib.validation.username.maxlength:Benutzername darf höchstens ${USERNAME_MAX_LENGTH}:max: Zeichen lang sein`,
    },
    {
      type: 'pattern',
      message: $localize`:@@lib.validation.username.pattern:Benutzername darf nur Buchstaben, Zahlen und Unterstriche enthalten`,
    },
  ],
  email: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.email.required:E-Mail erforderlich`,
    },
    {
      type: 'email',
      message: $localize`:@@common.validation.emailInvalid:Bitte geben Sie eine gültige E-Mail-Adresse ein`,
    },
    {
      type: 'alreadyInUse',
      message: $localize`:@@lib.validation.email.alreadyInUse:E-Mail bereits in Gebrauch`,
    },
    { type: 'unknown', message: UNKNOWN },
    { type: 'unauthenticated', message: CREDENTIALS_WRONG },
  ],
  confirmPassword: [
    { type: 'required', message: PASSWORD_REQUIRED },
    {
      type: 'matching',
      message: $localize`:@@lib.validation.password.matching:Passwort stimmt nicht überein`,
    },
  ],
  password: [
    { type: 'required', message: PASSWORD_REQUIRED },
    {
      type: 'minlength',
      message: $localize`:@@lib.validation.password.minlength:Mindestlänge ${PASSWORD_MIN_LENGTH}:min: Zeichen`,
    },
    { type: 'unauthenticated', message: CREDENTIALS_WRONG },
  ],
  name: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.name.required:Name erforderlich`,
    },
    { type: 'unknown', message: UNKNOWN },
    {
      type: 'minlength',
      message: $localize`:@@lib.validation.name.minlength:Name muss mindestens ${USERNAME_MIN_LENGTH}:min: Zeichen lang sein`,
    },
    {
      type: 'maxlength',
      message: $localize`:@@lib.validation.name.maxlength:Name darf höchstens ${USERNAME_MAX_LENGTH}:max: Zeichen lang sein`,
    },
    {
      type: 'pattern',
      message: $localize`:@@lib.validation.name.pattern:Name darf nur Buchstaben, Zahlen und Unterstriche enthalten`,
    },
  ],
  phone: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.phone.required:Telefonnummer erforderlich`,
    },
    {
      type: 'pattern',
      message: $localize`:@@lib.validation.phone.pattern:Ungültige Telefonnummer`,
    },
    { type: 'unknown', message: UNKNOWN },
  ],
  date: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.date.required:Datum erforderlich`,
    },
    { type: 'unknown', message: UNKNOWN },
  ],
  zipCode: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.zipCode.required:PLZ erforderlich`,
    },
    {
      type: 'pattern',
      message: $localize`:@@lib.validation.zipCode.pattern:Ungültige PLZ`,
    },
    { type: 'unknown', message: UNKNOWN },
  ],
  other: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.other.required:Eingabe erforderlich`,
    },
    { type: 'unknown', message: UNKNOWN },
  ],
  url: [
    {
      type: 'required',
      message: $localize`:@@lib.validation.url.required:URL erforderlich`,
    },
    {
      type: 'pattern',
      message: $localize`:@@lib.validation.url.pattern:Ungültige URL`,
    },
  ],
};

/**
 * Prüft, ob das Control denselben Wert trägt wie das Geschwister-Control
 * `matchTo`. Mit `reverse: true` triggert es stattdessen die Neuvalidierung
 * des Partner-Controls — so hängen Passwort und Passwortwiederholung
 * aneinander, ohne dass eine Seite veraltet stehen bleibt.
 */
export function matchValidator(matchTo: string, reverse?: boolean): ValidatorFn {
  return (control: AbstractControl) => {
    const controls = control.parent?.controls as
      | Record<string, AbstractControl>
      | undefined;

    if (control.parent && reverse) {
      controls?.[matchTo]?.updateValueAndValidity();
      return null;
    }

    return !!control.parent &&
      !!control.parent.value &&
      control.value === controls?.[matchTo]?.value
      ? null
      : { matching: true };
  };
}

/** Leere Werte bleiben gültig — dafür ist `Validators.required` zuständig. */
export function urlValidator(): ValidatorFn {
  const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
  return (control: AbstractControl) => {
    if (!control.value) {
      return null;
    }
    return urlPattern.test(control.value) ? null : { pattern: true };
  };
}
